import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export function virtualRange(count: number, top: number, height: number, rowHeight: number, overscan = 4) {
    const start = Math.max(0, Math.min(count, Math.floor(top / rowHeight) - overscan));
    return { start, end: Math.min(count, Math.ceil((top + height) / rowHeight) + overscan) };
}

/** Fixed-height rows keep rendering bounded even after many server pages are loaded. */
export function VirtualList<T>({ items, rowHeight = 40, label, render, itemKey, loadMore, hasMore = false, loading = false, total = items.length, empty = 'No matches. Try another search or filter.', className = '', onRangeChange }: {
    items: T[]; rowHeight?: number; label: string; render: (item: T, index: number) => ReactNode;
    itemKey: (item: T) => string; loadMore?: () => void; hasMore?: boolean; loading?: boolean;
    total?: number; empty?: ReactNode; className?: string; onRangeChange?: (start: number, end: number) => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [view, setView] = useState({ top: 0, height: 320 });
    const [focus, setFocus] = useState<number | null>(null);
    const requestedFocus = useRef<number | null>(null);
    const focusedRow = useRef<{element:HTMLElement;id:string;index:number;action:number} | null>(null);
    const range = virtualRange(items.length, view.top, view.height, rowHeight);
    useLayoutEffect(() => {
        const element = ref.current!;
        const update = () => setView({ top: element.scrollTop, height: element.clientHeight || 320 });
        update();
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        if (hasMore && !loading && range.end >= items.length - 4) loadMore?.();
    }, [range.end, items.length, hasMore, loading, loadMore]);
    useEffect(() => { onRangeChange?.(range.start, range.end); }, [range.start, range.end, onRangeChange]);
    useLayoutEffect(() => {
        if (focus === null || requestedFocus.current === null) return;
        const element = ref.current?.querySelector<HTMLElement>(`[data-virtual-index="${focus}"] :is(a,button,input,[tabindex])`);
        if (element) { element.focus({ preventScroll: true }); requestedFocus.current = null; }
    }, [focus, range.start, range.end]);
    useLayoutEffect(() => {
        const previous = focusedRow.current;
        if (!previous || previous.element.isConnected || document.activeElement !== document.body) return;
        const rows = ref.current?.querySelectorAll<HTMLElement>('[data-virtual-key]');
        const same = rows ? Array.from(rows).find(row => row.dataset.virtualKey === previous.id) : undefined;
        const adjacent = ref.current?.querySelector<HTMLElement>(`[data-virtual-index="${Math.max(range.start, Math.min(items.length - 1, previous.index))}"]`);
        const target = (same ?? adjacent)?.querySelectorAll<HTMLElement>('a,button,input,[tabindex]')[previous.action];
        (target ?? (same ?? adjacent)?.querySelector<HTMLElement>('a,button,input,[tabindex]') ?? ref.current)?.focus({preventScroll:true});
        focusedRow.current = null;
    }, [items, range.start, range.end]);
    const move = (index: number) => {
        const next = Math.max(0, Math.min(items.length - 1, index));
        const element = ref.current!;
        if (next * rowHeight < element.scrollTop) element.scrollTop = next * rowHeight;
        else if ((next + 1) * rowHeight > element.scrollTop + element.clientHeight) element.scrollTop = (next + 1) * rowHeight - element.clientHeight;
        setView({ top: element.scrollTop, height: element.clientHeight });
        requestedFocus.current = next;
        setFocus(next);
        const rendered = element.querySelector<HTMLElement>(`[data-virtual-index="${next}"] :is(a,button,input,[tabindex])`);
        if (rendered) { rendered.focus({ preventScroll: true }); requestedFocus.current = null; }
    };
    return <div className={`workspace-list ${className}`}>
        <div ref={ref} className="workspace-scroll" role="list" aria-label={label} aria-busy={loading} tabIndex={0}
            onFocusCapture={event => {
                const element=event.target as HTMLElement;
                const row=element.closest<HTMLElement>('[data-virtual-key]');
                focusedRow.current=row ? {element,id:row.dataset.virtualKey!,index:Number(row.dataset.virtualIndex),action:Array.from(row.querySelectorAll<HTMLElement>('a,button,input,[tabindex]')).indexOf(element)} : null;
            }}
            onBlurCapture={event => { if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) focusedRow.current=null; }}
            onScroll={e => {
                const element = e.currentTarget;
                const nextRange = virtualRange(items.length, element.scrollTop, element.clientHeight, rowHeight);
                const focusedRow = document.activeElement?.closest<HTMLElement>('[data-virtual-index]');
                if (focusedRow && element.contains(focusedRow) && (Number(focusedRow.dataset.virtualIndex) < nextRange.start || Number(focusedRow.dataset.virtualIndex) >= nextRange.end)) element.focus({ preventScroll: true });
                setView({ top: element.scrollTop, height: element.clientHeight });
            }}
            onKeyDown={e => {
                if (!items.length || (e.target instanceof HTMLInputElement) || (e.target instanceof HTMLSelectElement)) return;
                const row = (e.target as HTMLElement).closest<HTMLElement>('[data-virtual-index]');
                const index = row ? Number(row.dataset.virtualIndex) : Math.floor(view.top / rowHeight);
                const next = e.key === 'ArrowDown' ? index + 1 : e.key === 'ArrowUp' ? index - 1 : e.key === 'PageDown' ? index + Math.max(1, Math.floor(view.height / rowHeight)) : e.key === 'PageUp' ? index - Math.max(1, Math.floor(view.height / rowHeight)) : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : null;
                if (next !== null) { e.preventDefault(); move(next); }
            }}>
            <div className="workspace-virtual" style={{ height: Math.max(items.length * rowHeight, items.length ? 0 : 96) }}>
                {items.slice(range.start, range.end).map((item, offset) => {
                    const index = range.start + offset;
                    return <div key={itemKey(item)} className="workspace-virtual-row" role="listitem" aria-posinset={index + 1} aria-setsize={total} data-virtual-index={index} data-virtual-key={itemKey(item)}
                        style={{ top: index * rowHeight, height: rowHeight }}>{render(item, index)}</div>;
                })}
                {!items.length && <div className="workspace-empty" role="status">{loading ? 'Loading…' : empty}</div>}
            </div>
        </div>
        <div className="workspace-list-footer"><span role="status">{items.length ? `${Math.min(total, Math.floor(view.top / rowHeight) + 1)}–${Math.min(items.length, Math.ceil((view.top + view.height) / rowHeight))} of ${total.toLocaleString()}` : `${total.toLocaleString()} results`}</span>
            {hasMore && <button type="button" className="guild-link" disabled={loading} onClick={loadMore}>{loading ? 'Loading…' : 'Load more'}</button>}</div>
    </div>;
}
